import {
  addHours,
  addMinutes,
  addMonths,
  addDays,
  addYears,
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  format,
  getDate as getDateFns,
  getMonth,
  getYear,
  getHours,
  getMinutes,
  isAfter,
  parse,
  setDate as setDateFns,
  setHours,
  setMonth,
  setMinutes,
  setYear,
  setMilliseconds,
  setSeconds,
} from "date-fns";
import { immerable, produce } from "immer";

export const MINUTES_TO_ROUND = 15;
export const DATE_FORMAT = "yyyy-MM-dd";
export const TIME_FORMAT = "H:mm";

export const TimeTypes = {
  LEAVING_AT: "LEAVING_AT",
  ARRIVING_AT: "ARRIVING_AT",
  DEPART_NOW: "DEPART_NOW"
};

export const getDateTimeRoundedToMinutes = (date, roundTo = 1) => {
  let result = setMilliseconds(
    setSeconds(addMinutes(date, -(getMinutes(date) % roundTo) + roundTo), 0),
    0
  );
  return result;
};

const getDate = date => {
  return { day: getDateFns(date), month: getMonth(date), year: getYear(date) };
};

const setDate = (date, { day, month, year }) => {
  return (date = setYear(setMonth(setDateFns(date, day), month), year));
};

const getTime = date => {
  return { hours: getHours(date), minutes: getMinutes(date) };
};

const setTime = (date, { hours, minutes }) => {
  return (date = setHours(setMinutes(date, minutes), hours));
};

class DateTime {
  [immerable] = true;

  constructor(date) {
    this.dateTime = date ? new Date(date) : new Date();
    this.timeType = TimeTypes.LEAVING_AT;
  }

  addHours(hours = 0) {
    return produce(this, draft => {
      draft.dateTime = addHours(this.dateTime, hours);
    });
  }

  addMonths(months = 0) {
    return produce(this, draft => {
      draft.dateTime = addMonths(this.dateTime, months);
    });
  }

  addDays(days = 0) {
    return produce(this, draft => {
      draft.dateTime = addDays(this.dateTime, days);
    });
  }

  clone() {
    return produce(this, draft => {
      draft.dateTime = setDate(draft.dateTime, getDate(this.dateTime));
    });
  }

  departNow() {
    return produce(this, draft => {
      draft.dateTime = getDateTimeRoundedToMinutes(new Date(), MINUTES_TO_ROUND);
      draft.timeType = TimeTypes.DEPART_NOW;
    });
  }

  format(formatString) {
    return format(this.dateTime, formatString);
  }

  getTimeString() {
    return this.format(TIME_FORMAT);
  }

  getTimeType() {
    return this.timeType;
  }

  getDuration(other) {
    other = other.dateTime ?? other;
    let [start, end] = isAfter(this.dateTime, other) ? [other, this.dateTime] : [this.dateTime, other];
    let duration = {};

    duration.years = differenceInYears(end, start);
    start = addYears(start, duration.years);

    duration.months = differenceInMonths(end, start);
    start = addMonths(start, duration.months);

    duration.days = differenceInDays(end, start);
    start = addDays(start, duration.days);

    return duration;
  }

  isDepartingNow() {
    return this.timeType === TimeTypes.DEPART_NOW;
  }

  produce({ date, dateTime, time, timeType }) {
    return produce(this, draft => {
      if (dateTime) {
        draft.dateTime = dateTime.dateTime;
        draft.timeType = dateTime.timeType;
        return;
      }

      if (date) {
        draft.dateTime = setDate(draft.dateTime, getDate(date));

        if (this.isDepartingNow()) {
          draft.timeType = TimeTypes.LEAVING_AT;
          if (!time) {
            draft.dateTime = setTime(
              draft.dateTime,
              getTime(getDateTimeRoundedToMinutes(new Date(), MINUTES_TO_ROUND))
            );
          }
        }
      }

      if (typeof time === "string") {
        draft.dateTime = setTime(draft.dateTime, getTime(parse(time, TIME_FORMAT, new Date())));

        if (this.isDepartingNow()) {
          draft.timeType = TimeTypes.LEAVING_AT;
        }
      }

      if (timeType) {
        if (timeType === TimeTypes.DEPART_NOW) {
          // Change both date AND time to the current one with rounded minutes
          draft.dateTime = getDateTimeRoundedToMinutes(new Date(), MINUTES_TO_ROUND);
        }
        draft.timeType = timeType;
      }
    });
  }

  roundToMinutes(roundTo = MINUTES_TO_ROUND) {
    return produce(this, draft => {
      draft.dateTime = getDateTimeRoundedToMinutes(this.dateTime, roundTo);
    });
  }

  setLeavingAt() {
    return produce(this, draft => {
      draft.timeType = TimeTypes.LEAVING_AT;
    });
  }

  toDate() {
    return this.dateTime;
  }

  toString() {
    return `${this.dateTime} ${this.timeType}`;
  }
}

export default DateTime;
